import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Calculator, Laptop, Loader2, Monitor, Phone, Send, Smartphone, Tablet, Leaf } from 'lucide-react';
import { useLocalizedPath } from '../../../hooks/useLocalizedPath';
import { trackEvent } from '../../../lib/analytics';
import styles from './Co2Simulator.module.css';

type DeviceKey = 'laptops' | 'desktops' | 'smartphones' | 'tablets';

const CO2_FACTORS_KG: Record<DeviceKey, number> = {
  laptops: 133,
  desktops: 144,
  smartphones: 33,
  tablets: 54,
};

const KG_PER_KM_CAR = 0.12;

const DEVICE_ICONS: Record<DeviceKey, typeof Laptop> = {
  laptops: Laptop,
  desktops: Monitor,
  smartphones: Smartphone,
  tablets: Tablet,
};

const ORDER: DeviceKey[] = ['laptops', 'desktops', 'smartphones', 'tablets'];

function formatKg(kg: number, locale: string): string {
  if (kg >= 1000) {
    return `${(kg / 1000).toLocaleString(locale, { maximumFractionDigits: 2 })} t`;
  }
  return `${kg.toLocaleString(locale)} kg`;
}

function formatKm(km: number, locale: string): string {
  return Math.round(km).toLocaleString(locale);
}

export function Co2Simulator() {
  const { t, i18n } = useTranslation('reprise');
  const { lp } = useLocalizedPath();
  const [values, setValues] = useState<Record<DeviceKey, string>>({
    laptops: '',
    desktops: '',
    smartphones: '',
    tablets: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emptyHint, setEmptyHint] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const totals = useMemo(() => {
    const numeric: Record<DeviceKey, number> = {
      laptops: Math.max(0, parseInt(values.laptops, 10) || 0),
      desktops: Math.max(0, parseInt(values.desktops, 10) || 0),
      smartphones: Math.max(0, parseInt(values.smartphones, 10) || 0),
      tablets: Math.max(0, parseInt(values.tablets, 10) || 0),
    };
    const totalDevices = Object.values(numeric).reduce((s, n) => s + n, 0);
    const totalKg = ORDER.reduce((sum, key) => sum + numeric[key] * CO2_FACTORS_KG[key], 0);
    return { totalDevices, totalKg };
  }, [values]);

  const handleChange = (key: DeviceKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues((prev) => ({ ...prev, [key]: e.target.value }));
    if (submitted) setSubmitted(false);
    if (emptyHint && e.target.value) setEmptyHint(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    if (totals.totalDevices === 0) {
      setEmptyHint(true);
      setSubmitted(false);
      return;
    }
    setEmptyHint(false);
    setSubmitted(false);
    setLoading(true);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      trackEvent('lead_simulator_completed', {
        source: 'co2_simulator',
        total_devices: totals.totalDevices,
        total_co2_kg: totals.totalKg,
      });
    }, 900);
  };

  return (
    <form className={styles.simulator} onSubmit={handleSubmit} noValidate>
      <header className={styles.header}>
        <Leaf size={20} className={styles.headerIcon} aria-hidden="true" />
        <h2 className={styles.title}>{t('simulator.title')}</h2>
      </header>
      <p className={styles.description}>{t('simulator.description')}</p>

      <div className={styles.grid}>
        {ORDER.map((key) => {
          const Icon = DEVICE_ICONS[key];
          return (
            <label key={key} className={styles.field}>
              <span className={styles.label}>
                <Icon size={16} aria-hidden="true" />
                {t(`simulator.devices.${key}`)}
              </span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                placeholder="0"
                value={values[key]}
                onChange={handleChange(key)}
                className={styles.input}
                data-testid={`input-${key}`}
              />
            </label>
          );
        })}
      </div>

      <button
        type="submit"
        className={styles.button}
        disabled={loading}
        aria-disabled={totals.totalDevices === 0 ? true : undefined}
        data-testid="button-calculate-co2"
      >
        {loading ? (
          <>
            <Loader2 size={20} className={styles.spinner} aria-hidden="true" />
            {t('simulator.loading')}
          </>
        ) : (
          <>
            <Calculator size={20} aria-hidden="true" />
            {t('simulator.cta')}
          </>
        )}
      </button>

      {emptyHint && (
        <div className={styles.hint} role="alert">
          <AlertCircle size={16} aria-hidden="true" />
          <span>{t('simulator.emptyHint')}</span>
        </div>
      )}

      {submitted && totals.totalDevices > 0 && (
        <div className={styles.result} role="status" aria-live="polite">
          <span className={styles.resultLabel}>{t('simulator.resultLabel')}</span>
          <span className={styles.resultValue}>
            {formatKg(totals.totalKg, i18n.language)}
            <span className={styles.resultUnit}>{t('simulator.resultUnit')}</span>
          </span>
          <span className={styles.resultHint}>
            {t('simulator.resultHint', { count: totals.totalDevices })}
          </span>
          <span className={styles.resultEquiv}>
            {t('simulator.resultEquiv', { km: formatKm(totals.totalKg / KG_PER_KM_CAR, i18n.language) })}
          </span>

          <div className={styles.resultCta}>
            <p className={styles.resultCtaText}>{t('simulator.resultCtaText')}</p>
            <div className={styles.resultCtaActions}>
              <a
                href={lp('/contact')}
                className={styles.resultCtaPrimary}
                data-attr="cta-contact"
              >
                <Send size={14} aria-hidden="true" />
                {t('simulator.resultCtaForm')}
              </a>
              <a
                href="tel:+33279655611"
                className={styles.resultCtaPhone}
                data-attr="contact-phone"
              >
                <Phone size={14} aria-hidden="true" />
                02 79 65 56 11
              </a>
            </div>
          </div>
        </div>
      )}

      <p className={styles.source}>{t('simulator.source')}</p>
    </form>
  );
}
